import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { EmptyState, ListTable, Panel, StatusChip, type ListColumn, type StatusTone } from '../../../ui-kit';
import { fetchPcIntegrations, type PcIntegration } from '../../../services/policyCheckingService';
import { WarehouseLogo } from '../policy-servicing/logos';
import { fromNow, useLive } from '../rocco/live';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';

function stateOf(row: PcIntegration): { tone: StatusTone; label: string } {
  if (!row.connected) return { tone: 'neutral', label: 'Not connected' };
  if (row.error) return { tone: 'warning', label: 'Needs attention' };
  return { tone: 'good', label: 'Connected' };
}

/** Other ways the same documents could arrive, once they are wanted. */
const ALSO: Record<string, string> = {
  upload: 'A mailbox, SharePoint or Google Drive can feed the same check',
  warehouse: 'Also works with Redshift, Snowflake, BigQuery or Postgres',
};

/**
 * What Policy Checking is joined to: the documents people upload, and the
 * warehouse it compares the issued policy against.
 */
export default function IntegrationsPage() {
  const { data, error, loading } = useLive(fetchPcIntegrations, 15_000);
  if (error && !data) return <EmptyState message={error} />;

  const columns: ListColumn<PcIntegration>[] = [
    {
      key: 'name',
      header: 'System',
      grow: 1.2,
      minWidth: 190,
      render: (row) => (
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ display: 'flex', flexShrink: 0 }}>
            {row.id === 'warehouse'
              ? <WarehouseLogo name={row.name} />
              : <UploadFileRounded sx={(t) => ({ fontSize: 20, color: t.tokens.brand.primary })} />}
          </Box>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{row.name}</Typography>
        </Stack>
      ),
    },
    {
      key: 'role',
      header: 'Does',
      width: 140,
      render: (row) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{row.role}</Typography>,
    },
    {
      key: 'state',
      header: 'State',
      width: 150,
      render: (row) => <StatusChip {...stateOf(row)} />,
    },
    {
      key: 'activity',
      header: 'Lately',
      grow: 2,
      minWidth: 220,
      render: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
            {row.detail}{row.checked_at ? ` · last read ${fromNow(row.checked_at)}` : ''}
          </Typography>
          {row.error ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{row.error}</Typography>
          ) : (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{ALSO[row.id]}</Typography>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Panel title="Connected systems" subtitle="Documents in, our own records to compare against">
      <ListTable
        columns={columns}
        rows={data?.integrations ?? []}
        getRowKey={(row) => row.id}
        loading={loading}
        emptyMessage="Nothing connected."
      />
    </Panel>
  );
}
